import ReactGA from 'react-ga4';
import Config from '../../../client/actions/config/';
import Constants from './constants';
import ObjGeneral from '../../containers/function';

export default class EventTracker {
  constructor() {
    this.user = null;
    this.client = null;
    this.lastReqDate = null;
    this.ID_GOOGLE_ANALYTICS = 'G-XSSS4F1W4E';
    this.ID_GOOGLE_TAG_MANAGER = 'GTM-MGH4R2D';

    // if an instance has already been created, return it
    if (typeof EventTracker.instance === 'object') {
      return EventTracker.instance;
    }

    // create a singleton pattern instance
    EventTracker.instance = this;
    return this;
  }

  /**
   * Set user and client properties for tracking.
   * @param {string} user User information.
   * @param {string} client Client information.
   */
  setProperties(user, client) {
    this.user = user;
    this.client = client;

    if (!(typeof window === 'undefined') && Config.trackAnalytics === 'true') {
      // initializing Google Analytics and Tracking Pageviews
      ReactGA.initialize(this.ID_GOOGLE_ANALYTICS, {
        testMode: false,
        gaOptions: {
          userId: this.user,
          cookieDomain: 'none',
        }
      });
    }
  }

  /**
   * Set user-related information for tracking.
   * @param {object} payload Payload containing user-related data.
   * @returns {object} Updated payload with user information.
   */
  setInformationUserLoggedIn(payload) {
    payload = {
      ...payload,
      send_to: this.ID_GOOGLE_ANALYTICS,
      gc_username: this.user,
      gc_tenant: this.client,
    }

    return payload;
  }

  /**
   * Send an event to Google Analytics.
   * @param {string} eventName Name of the event.
   * @param {object} data Event data to be sent.
   */
  sendEventGA(eventName, data) {
    if (!(typeof window === 'undefined') && Config.trackAnalytics === 'true') {
      ReactGA.gtag('event', eventName, data);
    }
  }

  /**
   * Track when the full page has loaded.
   */
  trackPageFullLoaded() {
    const page = window.location.pathname + window.location.search;
    let eventData = {
      gc_page_title: this.getPageTitle(page)
    };
    eventData = this.setInformationUserLoggedIn(eventData);

    ReactGA.set({ page });
    ReactGA.send({ hitType: 'pageview', page });
    this.sendEventGA('gc_user_logged_in', eventData);
  }

  /**
   * Track page load time.
   * @param {object} payload Payload containing load time data (milliseconds and seconds).
   */
  trackPageLoadTime(payload) {
    const { milliseconds, seconds } = payload;
    let eventData = {
      gc_page_loadtime_milliseconds: milliseconds,
      gc_page_loadtime_seconds: seconds
    };
    eventData = this.setInformationUserLoggedIn(eventData);

    this.sendEventGA('gc_page_loadtime', eventData);
  }

  /**
   * Retrieves the title associated with a given page URL.
   * @param {string} page - The URL or identifier of the page.
   * @returns {string} The corresponding page title, or 'UNKNOWN' if no match is found.
   */
  getPageTitle(page) {
    // object that stores the correspondences between pages and titles
    const { pageTitles } = Constants;
    const noTitle = 'SIN-TITULO';

    // if the page name is not sent, it returns no title
    if (!page || page === '') {
      return noTitle;
    }

    // find the corresponding title in the pageTitles object
    for (const key in pageTitles) {
      if (page.includes(key)) {
        return pageTitles[key];
      }
    }

    // returns if the page visited is the start
    if (page === '/dashboard') {
      return 'Pagina-de-inicio';
    }

    // if no page title is found, display an alert on the screen
    alert(`This page does not have a title associated with it:\n${page}`);
    return noTitle;
  }

  /**
   * Tracks a custom event using Google Analytics.
   * @param {string} eventName - The name of the custom event.
   * @param {Object} payload - Additional data payload for the event.
   */
  trackCustomEvent(eventName, payload) {
    const eventData = this.setInformationUserLoggedIn(payload);
    this.sendEventGA(eventName, eventData);
  }

  /**
   * Tracks a Tasks Manager action event using Google Analytics.
   * @param {string} action - The action performed in the Tasks Manager.
   */
  trackTasksManagerAction(action) {
    const page = window.location.pathname + window.location.search;
    const {
      deviceType,
      isMobile,
      isDesktop,
      osName,
      osVersion,
      browserName,
      fullBrowserVersion,
      mobileModel
    } = ObjGeneral.getBrowserMetadata();

    let eventData = {
      gc_page_title: this.getPageTitle(page),
      gc_task_action: action,
      gc_task_isMobile: isMobile,
      gc_task_isDesktop: isDesktop,
      gc_task_osName: osName,
      gc_task_osVersion: osVersion,
      gc_task_browserName: browserName,
      gc_task_browserVersion: fullBrowserVersion,
      gc_task_deviceType: deviceType,
      gc_task_mobileModel: mobileModel
    };
    eventData = this.setInformationUserLoggedIn(eventData);

    this.sendEventGA('gc_tasks_manager_action', eventData);
  }

}


// WEBPACK FOOTER //
// ./source/client/containers/function/eventTracker.js